import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl as MuiFormControl,
  OutlinedInput as MuiOutlinedInput,
  InputLabel as MuiInputLabel,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  Grid as MuiGrid,
  Typography as MuiTypography,
  FormLabel as MuiFormLabel,
  Radio as MuiRadio,
  FormControlLabel as MuiFormControlLabel,
  Divider as MuiDivider,
  Button as MuiButton,
  Chip as MuiChip,
  RadioGroup as MuiRadioGroup,
  Switch as MuiSwitch,
} from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Loop as MuiLoopIcon } from "@mui/icons-material";
import {
  RecruitmentStatusOptions,
  IrbStatusOptions,
  MultiSite,
  defaultFUDateFrom,
  defaultDateTo,
  defaultRecruitmentDateFrom,
  StudyDefaults,
  SummaryAccrual,
  CancerNonCancer,
  GET_STUDY_CONTACTS_URL,
  GET_STUDY_TEAM_MEMBERS_URL,
  GET_STUDY_IRB_NUMBERS_URL,
  GET_STUDY_NICKNAMES_URL,
  GET_STUDY_PROTOCOL_NUMBER_URL,
  SelectHonestBrokerOption,
  STORAGE_NAMES,
  STORAGE_TYPE,
  RecruitmentStatus,
  YES_NO_FILTER_OPTION,
} from "app/shared/constants";
import { useUsers } from "common";
import { useFilters } from "app/services/filterService";
import { useStudies } from "app/services/studiesService";
import { setDefaultHB } from "app/shared/utils";
import { Autocomplete } from "app/shared/UI/Autocomplete/Autocomplete";
import { useBrowserStorage } from "app/services/StorageService/StorageHelper";
import { studyFiltersStyles } from "app/components/study/StudyFiltersStyles";
import { switchStyles } from "app/shared/UI/sharedStyles";
import { AutocompleteOption } from "app/shared/UI/Autocomplete";
import { isValid, format } from "date-fns";

const getReStatus = (studyFilters) =>
  studyFilters.reStatus ? studyFilters.reStatus.split(",") : null;

const getCancer = (studyFilters) =>
  studyFilters.cancer ? studyFilters.cancer : "all";

const getProtocolSummaryAccrual = (studyFilters) =>
  studyFilters.protocolSummaryAccrual
    ? studyFilters.protocolSummaryAccrual
    : "all";

const getPtlStatus = (studyFilters) =>
  studyFilters.ptlStatus ? studyFilters.ptlStatus.split(",") : null;

const getProtocolMultisiteTrial = (studyFilters) =>
  studyFilters.protocolMultisiteTrial
    ? studyFilters.protocolMultisiteTrial
    : "all";

const StudyFilterForm = () => {
  const { studyFilters, setPagination, setStudyFilters, impersonateUser } =
    useStudies();
  const { t } = useTranslation();
  const { currentUser } = useUsers();
  const {
    filterData: { studyDepartments, studyDivisions = {}, honestBrokers },
  } = useFilters();
  const { saveItem } = useBrowserStorage(STORAGE_TYPE.SESSION);

  // hooks, save initial value at state and setter
  const [irbNumber, setIrbNumber] = useState(studyFilters.irbNumber);
  const [nickname, setNickname] = useState(studyFilters.nickname);
  const [protocolNumber, setProtocolNumber] = useState(
    studyFilters.protocolNumber,
  );
  const [reStatus, setReStatus] = useState(getReStatus(studyFilters));
  const [cancer, setCancer] = useState(getCancer(studyFilters));

  const [studyFiltersValue, setStudyFiltersValue] = useState({
    department: studyFilters.dept,
    division: studyFilters.division,
    honestBroker: studyFilters.hbId,
    honestBrokerStatus:
      studyFilters.honestBrokerStatus || SelectHonestBrokerOption.SELECT_HB,
  });

  useEffect(() => {
    if (honestBrokers?.values?.length > 0) {
      setStudyFiltersValue((prevFilters) => ({
        ...prevFilters,
        honestBroker: prevFilters.honestBroker
          ? prevFilters.honestBroker
          : setDefaultHB(honestBrokers.values, currentUser),
      }));
    }
  }, [honestBrokers?.values, currentUser]);

  const [protocolSummaryAccrual, setProtocolSummaryAccrual] = useState(
    getProtocolSummaryAccrual(studyFilters),
  );
  const [protocolMultisiteTrial, setProtocolMultisiteTrial] = useState(
    getProtocolMultisiteTrial(studyFilters),
  );
  const [ptlStatus, setPtlStatus] = useState(getPtlStatus(studyFilters));

  // FOLLOW UP DATES FROM
  const [followUpDateFrom, setFollowUpDateFrom] = useState(
    studyFilters.fuDateFrom || null,
  );
  // FOLLOW UP DATES TO
  const [followUpDateTo, setFollowUpDateTo] = useState(
    studyFilters.fuDateTo || null,
  );

  // MIN-MAX FU DATE FROM
  const [minFUDateFrom] = useState(defaultFUDateFrom);
  const [maxFUDateFrom, setMaxFUDateFrom] = useState(defaultDateTo);

  // MIN-MAX FU DATE To
  const [minFUDateTo, setMinFUDateTo] = useState(defaultFUDateFrom);
  const [maxFUDateTo] = useState(defaultDateTo);

  const [suspended, setSuspended] = useState(studyFilters.suspended);
  const [studyTeamsSelfService, setStudyTeamsSelfService] = useState(
    studyFilters.selfService,
  );
  const [selectedStudyTeamMember, setSelectedStudyTeamMember] = useState(
    studyFilters.studyTeamMemberId,
  );

  const [selectedContact, setSelectedContact] = useState(
    studyFilters.contactStaffId,
  );

  // recruitment StartDate FROM
  const [recruitmentStartDateFrom, setRecruitmentStartDateFrom] = useState(
    studyFilters.reStartDateFrom || null,
  );
  // recruitment StartDate TO
  const [recruitmentStartDateTo, setRecruitmentStartDateTo] = useState(
    studyFilters.reStartDateTo || null,
  );

  // min-max ReSTART DATE FROM
  const [minReStartDateFrom] = useState(defaultRecruitmentDateFrom);
  const [maxReStartDateFrom, setMaxReStartDateFrom] = useState(defaultDateTo);

  // min-max ReSTART DATE TO
  const [minReStartDateTo, setMinReStartDateTo] = useState(
    defaultRecruitmentDateFrom,
  );
  const [maxReStartDateTo] = useState(defaultDateTo);

  // recruitment EndDate FROM
  const [recruitmentEndDateFrom, setRecruitmentEndDateFrom] = useState(
    studyFilters.reEndDateFrom || null,
  );
  // recruitment EndDate TO
  const [recruitmentEndDateTo, setRecruitmentEndDateTo] = useState(
    studyFilters.reEndDateTo || null,
  );

  // min-max End Date FROM
  const [minReEndDateFrom] = useState(defaultRecruitmentDateFrom);
  const [maxReEndDateFrom, setMaxReEndDateFrom] = useState(defaultDateTo);

  // min-max End Date TO
  const [minReEndDateTo, setMinReEndDateTo] = useState(
    defaultRecruitmentDateFrom,
  );
  const [maxReEndDateTo] = useState(defaultDateTo);

  // followUpDate FROM/TO Error
  const [followUpDateFromError, setFollowupDateFromError] = useState(false);
  const [followUpDateToError, setFollowupDateToError] = useState(false);

  // recruitment StartDate From/To Error
  const [recruitmentStartDateFromError, setRecruitmentStartDateFromError] =
    useState(false);
  const [recruitmentStartDateToError, setRecruitmentStartDateToError] =
    useState(false);

  // recruitment EndDate From/To Error
  const [recruitmentEndDateFromError, setRecruitmentEndDateFromError] =
    useState(false);
  const [recruitmentEndDateToError, setRecruitmentEndDateToError] =
    useState(false);

  const setFilterPreference = (updateFilterField) => {
    const updatedFiltervalues = {
      ...studyFilters,
      ...updateFilterField,
    };

    !impersonateUser &&
      saveItem(STORAGE_NAMES.PEP_STUDY_DASHBOARD_FILTERS, updatedFiltervalues);
    setPagination({
      page: StudyDefaults.STUDYDEFAULTPAGE,
      rowsPerPage: StudyDefaults.STUDYDEFAULTPAGESIZE,
    });
    setStudyFilters(updatedFiltervalues);
  };

  const handleResetStudyFilters = () => {
    // reset filters to default one

    setPagination({
      page: StudyDefaults.STUDYDEFAULTPAGE,
      rowsPerPage: StudyDefaults.STUDYDEFAULTPAGESIZE,
    });

    setStudyFiltersValue((prevFilters) => ({
      ...prevFilters,
      department: null,
      division: null,
      honestBroker: setDefaultHB(honestBrokers.values, currentUser),
      honestBrokerStatus: SelectHonestBrokerOption.SELECT_HB,
    }));

    setIrbNumber(null);
    setNickname(null);
    setProtocolNumber(null);
    setFollowUpDateFrom(null);
    setFollowUpDateTo(null);
    setReStatus([...StudyDefaults.STUDYDEFAULTSTATUS]);
    setSuspended(false);
    setStudyTeamsSelfService(null);
    setSelectedStudyTeamMember(null);
    setSelectedContact(null);
    setCancer("all");
    setProtocolSummaryAccrual("all");
    setProtocolMultisiteTrial("all");
    setPtlStatus(null);
    setRecruitmentStartDateFrom(null);
    setRecruitmentStartDateTo(null);
    setRecruitmentEndDateFrom(null);
    setRecruitmentEndDateTo(null);

    const initialValue = {
      reStatus: StudyDefaults.STUDYDEFAULTSTATUS.join(","),
      suspended: StudyDefaults.STUDYDEFAULTSUSPENDED,
      hbId: setDefaultHB(honestBrokers?.values, currentUser),
    };

    !impersonateUser &&
      saveItem(STORAGE_NAMES.PEP_STUDY_DASHBOARD_FILTERS, initialValue);
    setStudyFilters(initialValue);
  };

  const handleDisable = () =>
    ["ASSIGNED", "NOT_ASSIGNED", "UNKNOWN"].includes(
      studyFiltersValue.honestBrokerStatus,
    );

  const selectHonestBrokerHandler = (newValue) => {
    // function to find the key based on the value
    const getKeyFromValue = (value) => {
      return Object.keys(SelectHonestBrokerOption).find(
        (key) => SelectHonestBrokerOption[key] === value, // find the key that matches the value
      );
    };

    // update the state with the corresponding SelectHonestBrokerOption key,
    // or default to the "MuiSelect an Assigned Honest Broker" if not found
    setStudyFiltersValue((prevFilters) => ({
      ...prevFilters,
      honestBrokerStatus:
        newValue === SelectHonestBrokerOption.SELECT_HB
          ? SelectHonestBrokerOption.SELECT_HB
          : getKeyFromValue(newValue),
    }));

    // check if the newValue is one of the statuses that require clearing honestBroker
    if (
      [
        SelectHonestBrokerOption.ASSIGNED,
        SelectHonestBrokerOption.NOT_ASSIGNED,
        SelectHonestBrokerOption.UNKNOWN,
      ].includes(newValue)
    ) {
      setStudyFiltersValue((prevFilters) => ({
        ...prevFilters,
        honestBroker: null,
      }));
    } else {
      setStudyFiltersValue((prevFilters) => ({
        ...prevFilters,
        honestBroker: setDefaultHB(honestBrokers?.values, currentUser),
      }));
    }

    // FOR UNINITIATED
    if (
      [
        SelectHonestBrokerOption.NOT_ASSIGNED,
        SelectHonestBrokerOption.UNKNOWN,
      ].includes(newValue)
    ) {
      setReStatus((prevReStatus) => {
        // check if UNINITIATED is already in the statuss
        if (!prevReStatus.includes("UNINITIATED")) {
          return [...prevReStatus, "UNINITIATED"];
        }
        return prevReStatus;
      });
    } else if (
      [
        SelectHonestBrokerOption.SELECT_HB,
        SelectHonestBrokerOption.ASSIGNED,
      ].includes(newValue)
    ) {
      // if the selection is changed to something else, this will remove UNINITIATED if present
      setReStatus((prevReStatus) =>
        prevReStatus.filter((status) => status !== "UNINITIATED"),
      );
    }

    const finalRestatus = reStatus;

    const honestBrokerValue = (newValue) => {
      return Object.keys(SelectHonestBrokerOption).filter(
        (key) => SelectHonestBrokerOption[key] === newValue,
      );
    };

    const filterObject = {
      honestBrokerStatus: honestBrokerValue(newValue)[0] || null,
      hbId: null,
    };

    if (
      newValue === SelectHonestBrokerOption.UNKNOWN ||
      newValue === SelectHonestBrokerOption.NOT_ASSIGNED
    ) {
      if (!reStatus.includes(RecruitmentStatus.UNINITIATED)) {
        finalRestatus.push(RecruitmentStatus.UNINITIATED);

        filterObject.reStatus = finalRestatus.join(",");
      }
    } else if (
      newValue === SelectHonestBrokerOption.SELECT_HB ||
      newValue === SelectHonestBrokerOption.ASSIGNED
    ) {
      if (reStatus.includes(RecruitmentStatus.UNINITIATED)) {
        finalRestatus.forEach((status, index) => {
          if (status === RecruitmentStatus.UNINITIATED) {
            finalRestatus.splice(index, 1);
          }
        });
      }
      filterObject.reStatus = finalRestatus.join(",");
    }

    if (newValue === SelectHonestBrokerOption.SELECT_HB) {
      filterObject.honestBrokerStatus = null;
      filterObject.hbId = setDefaultHB(honestBrokers?.values, currentUser);
    }

    setFilterPreference(filterObject);
  };

  return (
    <MuiGrid container>
      <MuiGrid sx={studyFiltersStyles.customHeaderPadding} size={12}>
        <MuiTypography variant="h6" noWrap>
          {t("formLabel.studyFilterTitle")}
        </MuiTypography>
      </MuiGrid>
      <MuiGrid size={12}>
        <MuiDivider />
      </MuiGrid>
      <MuiGrid container size={12} justifyContent="flex-end">
        <div style={studyFiltersStyles.wrapper}>
          <MuiButton
            startIcon={<MuiLoopIcon fontSize="small" />}
            onClick={handleResetStudyFilters}
          >
            {t("formLabel.buttonResetFilter")}
          </MuiButton>
        </div>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="irb-number"
          getOptionLabel={(option) => option.displayText}
          label={t("formLabel.irbNumberFilterLabel")}
          value={irbNumber}
          onChange={(evt, value) => {
            setIrbNumber(value);
            setFilterPreference({ irbNumber: value ?? null });
          }}
          renderOption={(props, state, { inputValue }) => {
            return (
              <AutocompleteOption
                option={props}
                inputValue={inputValue}
                state={state}
              />
            );
          }}
          urlPrefix={GET_STUDY_IRB_NUMBERS_URL}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="study-protocolNumber"
          getOptionLabel={(option) => option.displayText}
          label={t("formLabel.protocolNumber")}
          value={protocolNumber}
          onChange={(evt, value) => {
            setProtocolNumber(value);
            setFilterPreference({ protocolNumber: value ?? null });
          }}
          renderOption={(props, state, { inputValue }) => {
            return (
              <AutocompleteOption
                option={props}
                inputValue={inputValue}
                state={state}
              />
            );
          }}
          urlPrefix={GET_STUDY_PROTOCOL_NUMBER_URL}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="selection-HonestBroker"
          key="selectionHonestBroker"
          label={t("formLabel.selectHonestBroker")}
          variant="outlined"
          margin="dense"
          options={Object.values(SelectHonestBrokerOption)}
          getOptionLabel={(option) => option}
          value={
            studyFiltersValue.honestBrokerStatus !==
            SelectHonestBrokerOption.SELECT_HB
              ? SelectHonestBrokerOption[studyFiltersValue.honestBrokerStatus]
              : SelectHonestBrokerOption.SELECT_HB
          }
          onChange={(evt, newValue) => selectHonestBrokerHandler(newValue)}
          clientSide={true}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="assigned-HonestBroker"
          key="assignedHonestBroker"
          label={t("formLabel.assignedHonestBroker")}
          variant="outlined"
          margin="dense"
          options={honestBrokers?.values || []}
          getOptionLabel={(option) => option.displayName || ""}
          value={
            (studyFiltersValue.honestBrokerStatus ===
              SelectHonestBrokerOption.SELECT_HB &&
              honestBrokers?.values?.find(
                (option) => option.id === studyFiltersValue.honestBroker,
              )) ||
            null
          }
          onChange={(evt, newValue) => {
            setStudyFiltersValue((prevFilters) => ({
              ...prevFilters,
              honestBroker: newValue ? newValue.id : "",
            }));

            studyFiltersValue.honestBrokerStatus ===
              SelectHonestBrokerOption.SELECT_HB &&
              setFilterPreference({ hbId: newValue ? newValue.id : null });
          }}
          clientSide={true}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          disabled={handleDisable()}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormControl fullWidth variant="outlined">
          <MuiInputLabel htmlFor="recruitment-status" shrink={true}>
            {t("formLabel.recruitmentStatusFilterLabel")}
          </MuiInputLabel>
          <MuiSelect
            value={reStatus || []}
            onChange={(e) => {
              setReStatus(e.target.value);
              setFilterPreference({
                reStatus: e.target.value.join(",") || null,
              });
            }}
            multiple
            size="small"
            input={
              <MuiOutlinedInput
                id="recruitment-status"
                name="recruitment-status"
                label={t("formLabel.recruitmentStatusFilterLabel")}
                margin="dense"
                notched
              />
            }
            renderValue={(selected) => (
              <div style={studyFiltersStyles.chipContainer}>
                {selected.map((value) => (
                  <MuiChip
                    sx={studyFiltersStyles.chip}
                    key={value}
                    label={t("recruitmentStatus." + value)}
                  />
                ))}
              </div>
            )}
          >
            {RecruitmentStatusOptions.map((statusOption) => (
              <MuiMenuItem key={statusOption} value={statusOption}>
                {t("recruitmentStatus." + statusOption)}
              </MuiMenuItem>
            ))}
          </MuiSelect>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="selection-studyTeamsSelfService"
          key="selectionstudyTeamsSelfService"
          label={t("formLabel.studyTeamsSelfService")}
          variant="outlined"
          margin="dense"
          options={YES_NO_FILTER_OPTION}
          getOptionLabel={(option) => option?.label || ""}
          value={
            YES_NO_FILTER_OPTION.find(
              (option) => option.code === studyTeamsSelfService,
            ) || null
          }
          onChange={(evt, newValue) => {
            const val = newValue !== null ? newValue.code : null;
            setStudyTeamsSelfService(val);
            setFilterPreference({
              selfService: val,
            });
          }}
          clientSide={true}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="study-team-members"
          getOptionLabel={(option) => option.displayText ?? ""}
          label={t("formLabel.studyTeamMembers")}
          value={selectedStudyTeamMember}
          onChange={(evt, value) => {
            setSelectedStudyTeamMember(value);
            setFilterPreference({ studyTeamMemberId: value || null });
          }}
          renderOption={(props, state, { inputValue }) => {
            return (
              <AutocompleteOption
                option={props}
                inputValue={inputValue}
                state={state}
              />
            );
          }}
          urlPrefix={GET_STUDY_TEAM_MEMBERS_URL}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormControl fullWidth variant="outlined">
          <MuiInputLabel htmlFor="irb-status" shrink={true} variant="outlined">
            {t("formLabel.irbStatus")}
          </MuiInputLabel>
          <MuiSelect
            value={ptlStatus || []}
            onChange={(e) => {
              setPtlStatus(e.target.value);
              setFilterPreference({
                ptlStatus: e.target.value.join(",") || null,
              });
            }}
            size="small"
            multiple
            input={
              <MuiOutlinedInput
                id="irb-status"
                label={t("formLabel.irbStatus")}
                labelwidth={1}
                margin="dense"
                placeholder={t("formLabel.irbStatus")}
                notched
              />
            }
            renderValue={(selected) => (
              <div style={studyFiltersStyles.chipContainer}>
                {selected.map((value) => (
                  <MuiChip
                    sx={studyFiltersStyles.chip}
                    key={value}
                    label={t("irbStatus." + value)}
                  />
                ))}
              </div>
            )}
          >
            {IrbStatusOptions.map((statusOption) => (
              <MuiMenuItem key={statusOption} value={statusOption}>
                {t("irbStatus." + statusOption)}
              </MuiMenuItem>
            ))}
          </MuiSelect>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid
        container
        sx={studyFiltersStyles.customAutocompletePadding}
        size={12}
      >
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              label={t("formLabel.followUpDateFrom")}
              format="MM/dd/yyyy"
              margin="dense"
              id="follow-up-date-start"
              value={followUpDateFrom}
              minDate={minFUDateFrom}
              maxDate={maxFUDateFrom}
              invalidDateMessage={t("formValidation.invalidDateFormat")}
              onChange={(value, { validationError }) => {
                setFollowUpDateFrom(value);
                setMinFUDateTo(value);

                validationError !== "maxDate" &&
                  setFilterPreference({
                    //fuDateFrom
                    fuDateFrom: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : followUpDateTo
                        ? defaultFUDateFrom
                        : null,
                    //fuDateTo
                    fuDateTo: isValid(value)
                      ? followUpDateTo
                        ? followUpDateTo
                        : defaultDateTo
                      : followUpDateTo
                        ? followUpDateTo
                        : null,
                  });

                if (validationError === null) setFollowupDateFromError(null);
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setFollowupDateFromError(t("formValidation.maxDateMessage"));
                } else if (error === "minDate") {
                  setFollowupDateFromError(
                    t("formValidation.minDateExceededError", {
                      minDateExceeded: format(minFUDateFrom, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setFollowupDateFromError(
                    t("formValidation.invalidDateFormat"),
                  );
                } else if (!error) {
                  setFollowupDateFromError(null);
                } else {
                  setFollowupDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(followUpDateFromError),
                  helperText: followUpDateFromError,
                },
              }}
            />
          </MuiGrid>
          <MuiGrid container justifyContent="center" size={1}>
            <span style={studyFiltersStyles.datePicker}>{" - "}</span>
          </MuiGrid>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              label={t("formLabel.followUpDateTo")}
              format="MM/dd/yyyy"
              margin="dense"
              id="follow-up-date-end"
              value={followUpDateTo}
              minDate={minFUDateTo}
              maxDate={maxFUDateTo}
              invalidDateMessage={t("invalidDateFormataa")}
              onChange={(value, { validationError }) => {
                setFollowUpDateTo(value);
                setMaxFUDateFrom(value);

                validationError !== "minDate" &&
                  setFilterPreference({
                    //fuDateTo
                    fuDateTo: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : followUpDateFrom
                        ? defaultDateTo
                        : null,
                    //fuDateFrom
                    fuDateFrom: isValid(value)
                      ? followUpDateFrom
                        ? followUpDateFrom
                        : minFUDateFrom
                      : followUpDateFrom
                        ? followUpDateFrom
                        : null,
                  });

                if (validationError === null) setFollowupDateToError(null);
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setFollowupDateToError(t("formValidation.minDateMessage"));
                } else if (error === "maxDate") {
                  setFollowupDateToError(
                    t("formValidation.maxDateExceededError", {
                      maxDateExceeded: format(maxFUDateTo, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setFollowupDateToError(t("formValidation.invalidDateFormat"));
                } else if (!error) {
                  setFollowupDateToError(null);
                } else {
                  setFollowupDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(followUpDateToError),
                  helperText: followUpDateToError,
                  size: "small",
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </MuiGrid>
        </MuiLocalizationProvider>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="study-dept"
          key="study-department"
          options={studyDepartments?.values || []}
          value={studyFiltersValue.department ?? null}
          getOptionLabel={(option) => option}
          clientSide={true}
          onChange={(evt, newValue) => {
            setStudyFiltersValue((prevFilters) => ({
              ...prevFilters,
              department: newValue,
            }));

            setFilterPreference({ dept: newValue || null });
          }}
          label={t("formLabel.department")}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          variant="outlined"
          margin="dense"
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="study-division"
          key="studyDivision"
          options={
            Array.isArray(studyDivisions?.values)
              ? studyDivisions.values.filter((option) => option !== null)
              : []
          }
          getOptionLabel={(option) => option}
          value={studyFiltersValue.division ?? null}
          clientSide={true}
          onChange={(evt, newValue) => {
            setStudyFiltersValue((prevFilters) => ({
              ...prevFilters,
              division: newValue,
            }));

            setFilterPreference({ division: newValue || null });
          }}
          label={t("formLabel.divisionFilterLabel")}
          variant="outlined"
          margin="dense"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="contact-name"
          getOptionLabel={(option) => option.displayText ?? ""}
          label={t("formLabel.contactName")}
          value={selectedContact}
          onChange={(evt, value) => {
            setSelectedContact(value);
            setFilterPreference({ contactStaffId: value || null });
          }}
          renderOption={(props, state, { inputValue }) => {
            return (
              <AutocompleteOption
                option={props}
                inputValue={inputValue}
                state={state}
              />
            );
          }}
          urlPrefix={GET_STUDY_CONTACTS_URL}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormControl fullWidth component="fieldset">
          <MuiFormLabel component="legend">
            {t("formLabel.summaryAccrual")}
          </MuiFormLabel>
          <MuiRadioGroup
            sx={studyFiltersStyles.radioGroup}
            name="protocolSummaryAccrual"
            value={protocolSummaryAccrual}
            onChange={(e) => {
              setProtocolSummaryAccrual(e.target.value);
              setFilterPreference({
                protocolSummaryAccrual: e.target.value || null,
              });
            }}
          >
            <MuiFormControlLabel
              value="all"
              control={<MuiRadio />}
              label={t("formLabel.protocolSummaryAccrualAll")}
            />
            <MuiFormControlLabel
              value={SummaryAccrual.SummaryAccrualTrue}
              control={<MuiRadio />}
              label={t("common.yesLabel")}
            />
            <MuiFormControlLabel
              value={SummaryAccrual.SummaryAccrualFalse}
              control={<MuiRadio />}
              label={t("common.noLabel")}
            />
          </MuiRadioGroup>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormControl fullWidth component="fieldset">
          <MuiFormLabel component="legend">
            {t("formLabel.cancerCenterFilterLabel")}
          </MuiFormLabel>
          <MuiRadioGroup
            sx={studyFiltersStyles.radioGroup}
            name="cancer-non-cancer"
            value={cancer}
            onChange={(e) => {
              setCancer(e.target.value);
              setFilterPreference({
                cancer: e.target.value || null,
              });
            }}
          >
            <MuiFormControlLabel
              value={CancerNonCancer.All}
              control={<MuiRadio />}
              label={t("formLabel.cancerCenterAll")}
            />
            <MuiFormControlLabel
              value={CancerNonCancer.Cancer}
              control={<MuiRadio />}
              label={t("formLabel.cancerCenterCancer")}
            />
            <MuiFormControlLabel
              value={CancerNonCancer.NonCancer}
              control={<MuiRadio />}
              label={t("formLabel.cancerCenterNon")}
            />
          </MuiRadioGroup>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <Autocomplete
          id="study-nickname"
          getOptionLabel={(option) => option.displayText}
          label={t("formLabel.studyNickname")}
          value={nickname}
          onChange={(evt, value) => {
            setNickname(value);
            setFilterPreference({ nickname: value || null });
          }}
          renderOption={(props, state, { inputValue }) => {
            return (
              <AutocompleteOption
                option={props}
                inputValue={inputValue}
                state={state}
              />
            );
          }}
          urlPrefix={GET_STUDY_NICKNAMES_URL}
        />
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiTypography component="div">
          <MuiFormLabel component="legend">
            {t("formLabel.suspended")}
          </MuiFormLabel>
          <MuiGrid
            id="suspendedSwitch"
            component="label"
            container
            alignItems="center"
            spacing={1}
          >
            <MuiGrid>{t("common.noLabel")}</MuiGrid>
            <MuiGrid>
              <MuiSwitch
                sx={switchStyles}
                checked={suspended}
                onChange={(evt) => {
                  setSuspended(evt.target.checked);
                  setFilterPreference({
                    suspended: evt.target.checked,
                  });
                }}
                value="suspended"
              />
            </MuiGrid>
            <MuiGrid>{t("common.yesLabel")}</MuiGrid>
          </MuiGrid>
        </MuiTypography>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormControl fullWidth component="fieldset">
          <MuiFormLabel component="legend">
            {t("formLabel.multiSite")}
          </MuiFormLabel>
          <MuiRadioGroup
            sx={studyFiltersStyles.radioGroup}
            name="multi-site"
            value={protocolMultisiteTrial}
            onChange={(e) => {
              setProtocolMultisiteTrial(e.target.value);
              setFilterPreference({
                protocolMultisiteTrial: e.target.value || null,
              });
            }}
          >
            <MuiFormControlLabel
              value="all"
              control={<MuiRadio />}
              label={t("formLabel.multiSiteAll")}
            />
            <MuiFormControlLabel
              value={MultiSite.MultiSiteFalse}
              control={<MuiRadio />}
              label={t("formLabel.multiSiteStanford")}
            />
            <MuiFormControlLabel
              value={MultiSite.MultiSiteTrue}
              control={<MuiRadio />}
              label={t("formLabel.multiSite")}
            />
          </MuiRadioGroup>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid sx={studyFiltersStyles.customAutocompletePadding} size={12}>
        <MuiFormLabel component="legend">
          {t("formLabel.hbRecruitment")}
        </MuiFormLabel>
      </MuiGrid>
      <MuiGrid
        container
        sx={studyFiltersStyles.customAutocompletePadding}
        size={12}
      >
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              clearable
              variant="inline"
              inputVariant="outlined"
              sx={studyFiltersStyles.labelWrapper}
              label={t("formLabel.recruitmentStartDateFrom")}
              format="MM/dd/yyyy"
              margin="dense"
              id="recruitment-start-date-from"
              value={recruitmentStartDateFrom}
              minDate={minReStartDateFrom}
              maxDate={maxReStartDateFrom}
              invalidDateMessage={t("formValidation.invalidDateFormat")}
              onChange={(value, { validationError }) => {
                setRecruitmentStartDateFrom(value);
                // use the date as date-to's minDate
                setMinReStartDateTo(value);

                validationError !== "maxDate" &&
                  setFilterPreference({
                    // reStartDateFrom
                    reStartDateFrom: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : recruitmentStartDateTo
                        ? defaultRecruitmentDateFrom
                        : null,
                    // reStartDateTo
                    reStartDateTo: isValid(value)
                      ? recruitmentStartDateTo
                        ? recruitmentStartDateTo
                        : defaultDateTo
                      : recruitmentStartDateTo
                        ? recruitmentStartDateTo
                        : null,
                  });

                if (validationError === null)
                  setRecruitmentStartDateFromError(null);
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setRecruitmentStartDateFromError(
                    t("formValidation.maxDateMessage"),
                  );
                } else if (error === "minDate") {
                  setRecruitmentStartDateFromError(
                    t("formValidation.minDateExceededError", {
                      minDateExceeded: format(minReStartDateFrom, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setRecruitmentStartDateFromError(
                    t("formValidation.invalidDateFormat"),
                  );
                } else if (!error) {
                  setRecruitmentStartDateFromError(null);
                } else {
                  setRecruitmentStartDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(recruitmentStartDateFromError),
                  helperText: recruitmentStartDateFromError,
                },
              }}
            />
          </MuiGrid>
          <MuiGrid container justifyContent="center" size={1}>
            <span style={studyFiltersStyles.datePicker}>{" - "}</span>
          </MuiGrid>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              variant="inline"
              inputVariant="outlined"
              clearable
              sx={studyFiltersStyles.labelWrapper}
              label={t("formLabel.recruitmentStartDateTo")}
              format="MM/dd/yyyy"
              margin="dense"
              id="recruitment-start-date-to"
              value={recruitmentStartDateTo}
              minDate={minReStartDateTo}
              maxDate={maxReStartDateTo}
              invalidDateMessage={t("formValidation.invalidDateFormat")}
              onChange={(value, { validationError }) => {
                setRecruitmentStartDateTo(value);
                // use the date to be maxDate of recruitmentStartDate date-from
                setMaxReStartDateFrom(value);

                validationError !== "minDate" &&
                  setFilterPreference({
                    // reStartDateTo
                    reStartDateTo: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : recruitmentStartDateFrom
                        ? defaultDateTo
                        : null,

                    // reStartDateFrom
                    reStartDateFrom: isValid(value)
                      ? recruitmentStartDateFrom
                        ? recruitmentStartDateFrom
                        : defaultRecruitmentDateFrom
                      : recruitmentStartDateFrom
                        ? recruitmentStartDateFrom
                        : null,
                  });
                if (validationError === null)
                  setRecruitmentStartDateToError(null);
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setRecruitmentStartDateToError(
                    t("formValidation.minDateMessage"),
                  );
                } else if (error === "maxDate") {
                  setRecruitmentStartDateToError(
                    t("formValidation.maxDateExceededError", {
                      maxDateExceeded: format(maxReStartDateTo, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setRecruitmentStartDateToError(
                    t("formValidation.invalidDateFormat"),
                  );
                } else if (!error) {
                  setRecruitmentStartDateToError(null);
                } else {
                  setRecruitmentStartDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(recruitmentStartDateToError),
                  helperText: recruitmentStartDateToError,
                },
              }}
            />
          </MuiGrid>
        </MuiLocalizationProvider>
      </MuiGrid>
      <MuiGrid
        container
        sx={studyFiltersStyles.customAutocompletePadding}
        size={12}
      >
        <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              variant="inline"
              inputVariant="outlined"
              sx={studyFiltersStyles.labelWrapper}
              label={t("formLabel.recruitmentEndDateFrom")}
              format="MM/dd/yyyy"
              margin="dense"
              id="recruitment-end-date-from"
              value={recruitmentEndDateFrom}
              minDate={minReEndDateFrom}
              maxDate={maxReEndDateFrom}
              clearable
              invalidDateMessage={t("formValidation.invalidDateFormat")}
              onChange={(value, { validationError }) => {
                setRecruitmentEndDateFrom(value);
                // use the date to be minDate of recruitmentStartDate date-to
                setMinReEndDateTo(value);

                validationError !== "maxDate" &&
                  setFilterPreference({
                    // reEndDateFrom
                    reEndDateFrom: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : recruitmentEndDateTo
                        ? defaultRecruitmentDateFrom
                        : null,
                    // reEndDateTo
                    reEndDateTo: isValid(value)
                      ? recruitmentEndDateTo
                        ? recruitmentEndDateTo
                        : defaultDateTo
                      : recruitmentEndDateTo
                        ? recruitmentEndDateTo
                        : null,
                  });
                if (validationError === null)
                  setRecruitmentEndDateFromError(null);
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setRecruitmentEndDateFromError(
                    t("formValidation.maxDateMessage"),
                  );
                } else if (error === "minDate") {
                  setRecruitmentEndDateFromError(
                    t("formValidation.minDateExceededError", {
                      minDateExceeded: format(minReEndDateFrom, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setRecruitmentEndDateFromError(
                    t("formValidation.invalidDateFormat"),
                  );
                } else if (!error) {
                  setRecruitmentEndDateFromError(null);
                } else {
                  setRecruitmentEndDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(recruitmentEndDateFromError),
                  helperText: recruitmentEndDateFromError,
                },
              }}
            />
          </MuiGrid>
          <MuiGrid container justifyContent="center" size={1}>
            <span style={studyFiltersStyles.datePicker}>{" - "}</span>
          </MuiGrid>
          <MuiGrid size={5.5}>
            <MuiDatePicker
              clearable
              variant="inline"
              inputVariant="outlined"
              label={t("formLabel.recruitmentEndDateTo")}
              format="MM/dd/yyyy"
              margin="dense"
              id="recruitment-end-date-to"
              value={recruitmentEndDateTo}
              minDate={minReEndDateTo}
              maxDate={maxReEndDateTo}
              invalidDateMessage={t("formValidation.invalidDateFormat")}
              onChange={(value, { validationError }) => {
                setRecruitmentEndDateTo(value);
                setMaxReEndDateFrom(value);

                validationError !== "minDate" &&
                  setFilterPreference({
                    // reEndDateTo
                    reEndDateTo: isValid(value)
                      ? format(new Date(value), "yyyy-MM-dd")
                      : recruitmentEndDateFrom
                        ? defaultDateTo
                        : null,
                    // reEndDateFrom
                    reEndDateFrom: isValid(value)
                      ? recruitmentEndDateFrom
                        ? recruitmentEndDateFrom
                        : defaultRecruitmentDateFrom
                      : recruitmentEndDateFrom
                        ? recruitmentEndDateFrom
                        : null,
                  });
                if (validationError === null)
                  setRecruitmentEndDateToError(null);
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setRecruitmentEndDateToError(
                    t("formValidation.minDateMessage"),
                  );
                } else if (error === "maxDate") {
                  setRecruitmentEndDateToError(
                    t("formValidation.maxDateExceededError", {
                      maxDateExceeded: format(maxReEndDateTo, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setRecruitmentEndDateToError(
                    t("formValidation.invalidDateFormat"),
                  );
                } else if (!error) {
                  setRecruitmentEndDateToError(null);
                } else {
                  setRecruitmentEndDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(recruitmentEndDateToError),
                  helperText: recruitmentEndDateToError,
                },
              }}
            />
          </MuiGrid>
        </MuiLocalizationProvider>
      </MuiGrid>
      <MuiGrid container size={12} justifyContent="flex-end">
        <div style={studyFiltersStyles.wrapper}>
          <MuiButton
            startIcon={<MuiLoopIcon fontSize="small" />}
            onClick={handleResetStudyFilters}
          >
            {t("formLabel.buttonResetFilter")}
          </MuiButton>
        </div>
      </MuiGrid>
    </MuiGrid>
  );
};

export default StudyFilterForm;
